import { NAV_AR, dirOf, en, pseudoTree, resources, type Language } from '@outss/i18n';
import i18n from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';

/**
 * The app's language (document 15, D2, section 5.3): the signed-in user's choice, mirrored in this
 * browser so the first paint is already in it. `<html lang dir>` follows it; Radix components read
 * the direction from the provider in main.tsx. Messages come from @outss/i18n.
 *
 * Development: localStorage `outss.pseudo` = "1" swaps English for the pseudo-locale, which shows
 * clipped layouts and strings that skip the catalogue.
 */
const KEY = 'outss.language';

export function storedLanguage(): Language {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'ar' || v === 'en') return v;
  } catch {
    // private mode or blocked storage: English
  }
  return 'en';
}

const pseudoOn = (() => {
  try {
    return import.meta.env.DEV && localStorage.getItem('outss.pseudo') === '1';
  } catch {
    return false;
  }
})();

void i18n.use(initReactI18next).init({
  resources: pseudoOn ? { ...resources, en: { translation: pseudoTree(en) } } : resources,
  lng: storedLanguage(),
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
  returnNull: false,
});

export function applyDocumentLanguage(lang: Language): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = lang;
  document.documentElement.dir = dirOf(lang);
}
applyDocumentLanguage(storedLanguage());

/** Switches the app's language now and remembers it in this browser. */
export async function setLanguage(lang: Language): Promise<void> {
  try {
    localStorage.setItem(KEY, lang);
  } catch {
    // remembered for this page only
  }
  applyDocumentLanguage(lang);
  if (i18n.language !== lang) await i18n.changeLanguage(lang);
}

/** The language the app is in (re-renders when it changes). */
export function useLanguage(): Language {
  const { i18n: i } = useTranslation();
  return i.language === 'ar' ? 'ar' : 'en';
}

/** `t` for the current language, with keys typed against the English catalogue. */
export function useT() {
  return useTranslation().t;
}

/** A navigation or menu label (app/navigation.ts) in the reader's language. */
export function navLabel(label: string, lang: Language): string {
  return lang === 'ar' ? (NAV_AR[label] ?? label) : label;
}

export { i18n };

declare module 'i18next' {
  interface CustomTypeOptions {
    defaultNS: 'translation';
    resources: { translation: typeof en };
  }
}
